import { useGetFrontTipiDomandaIdCompetenze } from '@framework/api-client/api/inquiries-compile/domande-controller/domande-controller.ts';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { SelectWidgetWrapper } from '@framework/engine/schema/widgets/repository/inputs/common/select-widget/SelectWidgetWrapper.tsx';
import { getWidgetProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { useField } from '@framework/form/field/useField.tsx';
import { useController } from '@framework/form/hooks/useController.ts';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { Spin, Typography } from 'antd';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFillableSubmissionContext } from '../../FillableSubmissionContext.tsx';
import { competenceSelectWidgetLabelName } from './createCompetenceSelectFormSchema.ts';
import { type ICompetenceWidget } from './ICompetenceWidget.ts';

type ICompetenceWidgetComponentProps = IWidgetComponentProps<ICompetenceWidget>;

export function CompetenceWidgetComponent(
  props: ICompetenceWidgetComponentProps,
) {
  const { widget } = props;

  const widgetOptions = getWidgetProps(props);
  const { submission } = useFillableSubmissionContext();
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetFrontTipiDomandaIdCompetenze(
    submission?.idTipoDomanda ?? 'na',
    {
      query: {
        enabled: submission?.idTipoDomanda != null && !props.readonly,
        keepPreviousData: true,
      },
    },
  );

  const labelFieldName = useMemo(
    () => competenceSelectWidgetLabelName(widgetOptions.name),
    [widgetOptions.name],
  );

  const { field: labelField } = useController(labelFieldName);
  const { setValue: setLabelFieldValue } = useField(labelFieldName);

  // Campo label precedentemente selezionato
  const [labelValue, idValue] = useWatch({
    name: [labelFieldName, widgetOptions.name],
  });

  // Costruisco le opzioni della select
  const options = useMemo(() => {
    if (!data || props.readonly) {
      // Ritorno comunque il valore selezionato.
      return [{ label: labelValue, value: idValue }];
    }

    return (
      data
        ?.map(competence => ({
          label: competence.denominazione,
          value: competence.idCompetenza!,
          vectorLayerCode: competence.codiceIdentificativoLayerVettoriale,
        }))
        // Ordino le opzioni per label
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  }, [data, props.readonly, labelValue, idValue]);

  // Sincronizzo il campo label con il campo ID; specie in caso di valore di default
  useEffect(() => {
    if (!idValue || props.readonly) return;
    const matchedOption = options.find(o => o.value === idValue);

    // Aggiorno il campo label se non corrisponde a quello selezionato.
    // Questo capita quando si seleziona una competenza automaticamente
    // (valore di default) e il campo label non è ancora stato aggiornato.
    if (matchedOption?.label && matchedOption.label !== labelValue) {
      setLabelFieldValue(matchedOption.label, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [idValue, labelValue, options, props.readonly, setLabelFieldValue]);

  return (
    <SelectWidgetWrapper
      {...props}
      hidden={widget.layout?.hide && !props.preview}
    >
      <SelectInput
        readOnly={props.readonly}
        name={widgetOptions.name}
        placeholder={widgetOptions.placeholder}
        allowClear
        showSearch
        filterOption
        optionFilterProp="label"
        onChange={(value, option, onChange) => {
          onChange(value);
          labelField.onChange((option as any)?.label ?? null);
          labelField.onBlur();
        }}
        notFoundContent={
          isLoading ? (
            <Spin size="small" />
          ) : (
            t('competence-widget.no-element-found', 'Nessun elemento trovato.')
          )
        }
        loading={isLoading}
        options={options}
      />

      {error && !props.readonly && (
        <Typography.Text type="danger">
          {t(
            'competence-widget.error-fetching-competences',
            'Impossibile recuperare i dati delle Competenze. Riprovare più tardi.',
          )}
        </Typography.Text>
      )}
    </SelectWidgetWrapper>
  );
}
